import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bot, Plus } from "lucide-react";
import { useTenant } from "@/context/tenant-context";
import { useWorkspace } from "@/context/workspace-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { useAgentAccess } from "@/hooks/use-agent-access";
import { agentTasksApi, type AgentTask } from "@/api/agent-tasks";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";
import { NewTaskForm } from "@/components/tasks/new-task-form";
import { TaskDetailDrawer } from "@/components/tasks/task-detail-drawer";
import { TaskList } from "@/components/tasks/task-list";

/** Top-bar Tasks view: the chat's agent, same list as every other Tasks view. */
export function TasksSurface() {
  const { tenantId } = useTenant();
  const { threadContext } = useWorkspace();
  const agentId = threadContext.agentId;
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const selectedAgent = agents.find((a) => a.id === agentId);
  const { canManage } = useAgentAccess(agentId);
  const [selectedTask, setSelectedTask] = useState<AgentTask | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  // Keyed under queryKeys.agentTasks (the "agent-tasks" root), so an edit in
  // the drawer or any other view refreshes this list too. It used its own key
  // before, and edits made in the drawer did not show here until a reload.
  const { data: tasks = [], isLoading } = useQuery({
    queryKey: queryKeys.agentTasks.byAgent(agentId ?? "none"),
    queryFn: () => agentTasksApi.list(tenantId, { agentId: agentId! }),
    enabled: !!tenantId && !!agentId,
  });

  if (!agentId) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Bot size={40} className="mx-auto mb-3 text-text-muted" />
          <p className="text-sm text-text-secondary">Select an agent to view their tasks</p>
        </div>
      </div>
    );
  }

  const recurring = tasks.filter((t) => t.type === "recurring").length;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="min-h-14 shrink-0 border-b border-border bg-surface px-6 py-2 lg:px-10">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-text-primary">
              {selectedAgent?.name ?? "Agent"} Tasks
            </h2>
            <p className="text-xs text-text-muted">
              {recurring} recurring · {tasks.length - recurring} one-off
            </p>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
              <Plus size={14} className="mr-1" />
              New Task
            </Button>
          )}
        </div>
      </div>

      <div className="chat-scroll flex-1 overflow-y-auto px-6 py-4 lg:px-10">
        <div className="mx-auto max-w-5xl space-y-3">
          {showCreate && <NewTaskForm agentId={agentId} onDone={() => setShowCreate(false)} />}
          {isLoading ? (
            <p className="py-8 text-center text-sm text-text-muted">Loading tasks...</p>
          ) : (
            <TaskList
              tasks={tasks}
              onOpen={setSelectedTask}
              emptyText="This agent has no tasks yet."
            />
          )}
        </div>
      </div>

      <TaskDetailDrawer task={selectedTask} tenantId={tenantId} onClose={() => setSelectedTask(null)} />
    </div>
  );
}
